import { useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Expand } from 'lucide-react'
import { cn } from '@/lib/utils'
import ImageViewerDialog, {
  ImageViewerItem,
} from '../../../../components/ImageViewerDialog'

const ScreenshotShowcase = ({
  screenshots,
}: {
  screenshots: ImageViewerItem[]
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [direction, setDirection] = useState(0)
  const [viewerOpen, setViewerOpen] = useState(false)
  const reduceMotion = useReducedMotion()
  const multiple = screenshots.length > 1

  const goTo = (index: number) => {
    setDirection(index > activeIndex ? 1 : -1)
    setActiveIndex(index)
  }

  const activeScreenshot = screenshots[activeIndex]

  return (
    <div className='flex w-full flex-col gap-0'>
      <div className='relative aspect-3112/2294 w-full overflow-hidden'>
        <AnimatePresence initial={false} mode='sync'>
          <motion.div
            key={activeIndex}
            className='absolute inset-0 cursor-pointer'
            initial={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 1, x: `${direction * 100}%` }
            }
            animate={{ opacity: 1, x: 0 }}
            exit={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 1, x: `${direction * -100}%` }
            }
            transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
            onClick={() => setViewerOpen(true)}
          >
            <img
              src={activeScreenshot.url}
              alt={activeScreenshot.alt ?? activeScreenshot.title ?? ''}
              className='h-full w-full object-cover'
            />
          </motion.div>
        </AnimatePresence>
      </div>

      <button
        type='button'
        onClick={() => setViewerOpen(true)}
        className='mb-4 -mt-4 px-6 flex items-center justify-between gap-2 text-xs text-muted-foreground hover:text-foreground'
      >
        <span className='truncate'>{activeScreenshot.title}</span>
        <Expand className='h-3.5 w-3.5 shrink-0' />
      </button>

      {multiple ? (
        <div className='flex flex-wrap gap-2 px-6'>
          {screenshots.map((screenshot, index) => (
            <button
              key={`tab-${screenshot.url}-${index}`}
              type='button'
              onClick={() => goTo(index)}
              className={cn(
                'aspect-3112/2294 w-20 overflow-hidden rounded-md border transition-all',
                index === activeIndex
                  ? 'border-primary ring-1 ring-primary'
                  : 'border-muted-foreground/40 opacity-70 hover:border-muted-foreground/70 hover:opacity-100',
              )}
            >
              <img
                src={screenshot.url}
                alt={screenshot.alt ?? screenshot.title ?? ''}
                className='h-full w-full scale-110 object-cover'
              />
            </button>
          ))}
        </div>
      ) : null}

      {viewerOpen ? (
        <ImageViewerDialog
          items={screenshots}
          initialIndex={activeIndex}
          open={viewerOpen}
          onOpenChange={setViewerOpen}
        />
      ) : null}
    </div>
  )
}

export default ScreenshotShowcase
